<template>
  <AdminLayout>
    <div class="flex justify-center">
      <div class="w-full max-w-lg">
        <h2 class="text-xl font-semibold text-center mb-6">
          {{ isEditMode ? 'Edit Customer Invite' : 'New Customer Invite' }}
        </h2>

        <div v-if="isLoading" class="flex justify-center my-8">
          <div class="loader">Loading...</div>
        </div>

        <div v-else class="space-y-4">
          <TextInput v-model="formData.team_name" label="Team Name" placeholder="Enter team name" />

          <TextInput
            v-model="formData.contact_email"
            label="Primary Email"
            placeholder="Enter primary email"
            type="email"
          />

          <SelectMenu
            label="Select a plan"
            :options="planOptionLabels"
            :initial-value="getSelectedPlanLabel"
            @selected-option="handlePlanSelection"
          />

          <SelectMenu
            label="Select a coupon (optional)"
            :options="couponOptionLabels"
            :initial-value="getSelectedCouponLabel"
            @selected-option="handleCouponSelection"
          />

          <TextInput v-model="formData.initial_ad_spend" label="Initial Ad Spend ($)" placeholder="500" />

          <div class="flex justify-center mt-6">
            <TButton
              :disabled="isLoading || !isFormValid"
              color="indigo"
              :label="isEditMode ? 'Update Invite' : 'Create Invite'"
              @click="saveInvite"
            />
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AdminLayout from '../AdminLayout/index.vue';
import TextInput from '../shared/TextInput.vue';
import SelectMenu from '../shared/SelectMenu.vue';
import TButton from '../shared/TButton.vue';
import type { CustomerInvite } from '../../types/customerInvite';
import { listPlatformFeePlans, listPlatformFeeCoupons } from '../../api/billing';
import type { BillingPlatformFeePlan, BillingPlatformFeeCoupon } from '../../api/types/Billing';

const route = useRoute();
const router = useRouter();
const store = useStore();

interface FormData {
  team_name: string;
  contact_email: string;
  stripe_plan_id: string;
  stripe_coupon_id: string;
  initial_ad_spend: string;
}

const formData = ref<FormData>({
  team_name: '',
  contact_email: '',
  stripe_plan_id: '',
  stripe_coupon_id: '',
  initial_ad_spend: '',
});

const planOptions = ref<BillingPlatformFeePlan[]>([]);
const couponOptions = ref<BillingPlatformFeeCoupon[]>([]);

const planOptionLabels = computed<string[]>(() => {
  return planOptions.value.map(option => option.name);
});

const couponOptionLabels = computed<string[]>(() => {
  return couponOptions.value.map(option => option.name);
});

const getSelectedPlanLabel = computed<string>(() => {
  const option = planOptions.value.find(opt => opt.id === formData.value.stripe_plan_id);
  return option ? option.name : 'Select a plan';
});

const getSelectedCouponLabel = computed<string>(() => {
  const option = couponOptions.value.find(opt => opt.id === formData.value.stripe_coupon_id);
  return option ? option.name : 'No coupon';
});

const handlePlanSelection = (label: string): void => {
  const option = planOptions.value.find(opt => opt.name === label);
  if (option) {
    formData.value.stripe_plan_id = option.id;
  }
};

const handleCouponSelection = (label: string): void => {
  const option = couponOptions.value.find(opt => opt.name === label);
  if (option) {
    formData.value.stripe_coupon_id = option.id;
  }
};

const isLoading = ref<boolean>(false);
const errorMessage = ref<string>('');
const inviteId = ref<string | null>(null);

const isEditMode = computed<boolean>(() => !!route.params.id);

const isFormValid = computed<boolean>(
  () =>
    formData.value.team_name.trim() !== '' &&
    formData.value.contact_email.trim() !== '' &&
    formData.value.stripe_plan_id.trim() !== '' &&
    formData.value.initial_ad_spend.trim() !== ''
);

const fetchInviteData = async (): Promise<void> => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await fetch(`/api/admin/customer_invites/${inviteId.value}`);
    const data = await response.json();

    if (response.ok && data.record) {
      const record: CustomerInvite = data.record;
      formData.value = {
        team_name: record.team_name || '',
        contact_email: record.contact_email || '',
        stripe_plan_id: record.stripe_plan_id || '',
        stripe_coupon_id: record.stripe_coupon_id || '',
        initial_ad_spend: record.initial_ad_spend ? (record.initial_ad_spend / 100).toString() : '',
      };
    } else {
      errorMessage.value = 'Failed to load customer invite data';
      console.error('Failed to load data:', data);
    }
  } catch (error) {
    console.error('Error fetching customer invite:', error);
    errorMessage.value = 'An error occurred while loading customer invite data';
  } finally {
    isLoading.value = false;
  }
};

const saveInvite = async (): Promise<void> => {
  isLoading.value = true;
  errorMessage.value = '';

  if (isNaN(parseFloat(formData.value.initial_ad_spend))) {
    errorMessage.value = 'Initial ad spend must be a valid number';
    isLoading.value = false;
    return;
  }

  const initialAdSpendCents = Math.round(parseFloat(formData.value.initial_ad_spend) * 100);

  const requestBody = {
    team_name: formData.value.team_name,
    contact_email: formData.value.contact_email,
    stripe_plan_id: formData.value.stripe_plan_id,
    ...(formData.value.stripe_coupon_id && formData.value.stripe_coupon_id !== ''
      ? { stripe_coupon_id: formData.value.stripe_coupon_id }
      : {}),
    initial_ad_spend: initialAdSpendCents,
  };

  try {
    const url = isEditMode.value ? `/api/admin/customer_invites/${inviteId.value}` : '/api/admin/customer_invites';

    const method = isEditMode.value ? 'PUT' : 'POST';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const response = await fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (response.ok && data.status === 'success') {
      store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: isEditMode.value ? 'Invite Updated' : 'Invite Created',
        alertDescription: isEditMode.value
          ? 'Customer invite updated successfully!'
          : 'Customer invite created successfully!',
      });

      redirectToList();
    } else {
      store.commit('general/showAlert', {
        alertType: 'error',
        alertTitle: 'Error',
        alertDescription: data.message || `Failed to ${isEditMode.value ? 'update' : 'create'} customer invite`,
      });
      errorMessage.value = data.message || `Failed to ${isEditMode.value ? 'update' : 'create'} customer invite`;
    }
  } catch (error) {
    console.error(`Error ${isEditMode.value ? 'updating' : 'creating'} customer invite:`, error);
    errorMessage.value = `An error occurred while ${isEditMode.value ? 'updating' : 'creating'} the customer invite`;

    store.commit('general/showAlert', {
      alertType: 'error',
      alertTitle: 'Error',
      alertDescription: `An error occurred while ${isEditMode.value ? 'updating' : 'creating'} the customer invite`,
    });
  } finally {
    isLoading.value = false;
  }
};

const redirectToList = (): void => {
  router.push('/admin/customer_invites');
};

const fetchPlanOptions = async (): Promise<void> => {
  const response = await listPlatformFeePlans();
  if (response?.records) {
    planOptions.value = response.records;
  }
};

const fetchCouponOptions = async (): Promise<void> => {
  const response = await listPlatformFeeCoupons();
  if (response?.records) {
    couponOptions.value = response.records;
  }
};

onMounted(async () => {
  await fetchPlanOptions();
  await fetchCouponOptions();

  if (isEditMode.value) {
    inviteId.value = route.params.id as string;
    fetchInviteData();
  }
});
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
